@file:NoLiveLiterals

package com.example.borshi.web.home

import androidx.compose.runtime.NoLiveLiterals
import com.example.borshi.web.model.Category
import com.example.borshi.web.model.NutritionistBasePlan
import com.example.borshi.web.model.NutritionistProfile
import com.example.borshi.web.model.Product
import com.example.borshi.web.firebase.FirebaseService

// ─────────────────────────────────────────────────────────────────────────────
// Use-Cases — thin orchestration layer.
// Each class has a single responsibility; easy to test in isolation.
// ─────────────────────────────────────────────────────────────────────────────

/** Fetch the displayed category list, with fallback handled inside the repo. */
class GetCategoriesUseCase(
    private val repository: ProductRepository = FirebaseProductRepository()
) {
    suspend operator fun invoke(): Result<List<Category>> =
        repository.getCategories()
}

/** Fetch the homepage featured products (capped to [AppConfig.FEATURED_ITEMS_LIMIT]). */
class GetFeaturedProductsUseCase(
    private val repository: ProductRepository = FirebaseProductRepository(),
    private val limit: Int = AppConfig.FEATURED_ITEMS_LIMIT
) {
    suspend operator fun invoke(): Result<List<Product>> =
        repository.getFeaturedProducts(limit)
}

/** Fetch all available nutritionists. */
class GetNutritionistsUseCase(
    private val repository: NutritionRepository = FirebaseNutritionRepository()
) {
    suspend operator fun invoke(): Result<List<NutritionistProfile>> =
        repository.getNutritionists()
}

/** Fetch public nutrition plans. */
class GetPublicPlansUseCase(
    private val repository: NutritionRepository = FirebaseNutritionRepository()
) {
    suspend operator fun invoke(): Result<List<NutritionistBasePlan>> =
        repository.getPublicPlans()
}

/** Calculate seconds remaining until the 8 PM pre-order deadline. */
class GetUrgencyTimerUseCase {
    operator fun invoke(): UrgencyTimerState {
        val now: dynamic = kotlin.js.Date()
        val dl: dynamic  = kotlin.js.Date()
        dl.setHours(AppConfig.PRE_ORDER_DEADLINE_HOUR, AppConfig.PRE_ORDER_DEADLINE_MINUTE, 0, 0)
        if ((now.getTime() as Double) >= (dl.getTime() as Double)) {
            dl.setDate(dl.getDate() + 1)
        }
        val secondsRemaining =
            ((dl.getTime() as Double - now.getTime() as Double) / AppConfig.MS_PER_SECOND)
                .toLong()
                .coerceAtLeast(0L)
        return UrgencyTimerState(secondsRemaining)
    }
}

/** Fetch all customer feedback images from Firebase Storage feedback/ folder. */
class GetFeedbackImagesUseCase {
    suspend operator fun invoke(): List<String> =
        try { FirebaseService.getFeedbackImageUrls() } catch (_: Throwable) { emptyList() }
}
